import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Layout, Col, Row, Space } from 'antd';
import * as d3 from 'd3';
import ChartTooltip from '../chart-tooltip/chart-tooltip.js';
import DownloadAsPNGButton from '../download-png-btn/download-as-png-btn.js';
import DownloadAsExcelButton from '../download-as-excel-btn/download-as-excel-btn.js';
import { formatFilters } from '../../utils/format-filters.js';

import './multy-line-chart.css';

const LineChart = (props) => {
  const {
    id,
    data,
    xTicksFormatter,
    hasLegend,
    colors,
    fileName,
    columns,
    filters,
    title,
  } = props;

  const [tooltipTop, setTooltipTop] = useState('auto');
  const [tooltipLeft, setTooltipLeft] = useState('auto');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState('');
  const [tooltipValue, setTooltipValue] = useState('');

  const [legend, setLegend] = useState([]);

  const [width, setWidth] = useState(0);

  const margin = { top: 16, right: 44, bottom: 28, left: 48 },
    height =
      window.height < 200
        ? 150 - margin.bottom - margin.top
        : 200 - margin.bottom - margin.top,
    bisect = d3.bisector(function (d) {
      return d.key;
    }).left;

  const colorScale = d3
    .scaleOrdinal()
    .domain(['Positive', 'Negative'])
    .range(['#6FCF97', '#EB5757']);

  function initChart() {
    const initialCalculatedWidth =
      document.getElementById(`chart-${id}`).getBoundingClientRect().width -
      margin.left -
      margin.right;
    const initialWidth =
      window.innerWidth <= 768 ? 500 : initialCalculatedWidth;

    let svg = d3
      .select(`#chart-${id}`)
      .append('svg')
      .attr('width', initialWidth + margin.left + margin.right)
      .attr('height', height + margin.bottom + margin.top + 20)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // legend container
    const legendContainer = d3
      .select(`#chart-${id}`)
      .append('div')
      .attr('class', 'legend-container')
      .style('display', 'flex')
      .style('justify-content', 'center')
      .style('align-items', 'center')
      .style('margin-top', '5px');

    // Add legend items
    colorScale.domain().forEach((group) => {
      const legendItem = legendContainer
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('margin-right', '25px');

      legendItem
        .append('div')
        .style('width', '10px')
        .style('height', '10px')
        .style('background-color', colorScale(group))
        .style('margin-right', '5px');

      legendItem
        .append('span')
        .text(group)
        .style('font-size', '12px')
        .style('color', '#fff');
    });

    // Remove margin.left from x-axis transform
    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`);

    svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(0, 0)`);

    svg.append('g').attr('class', 'chart-container');

    svg
      .append('g')
      .attr('class', 'focus')
      .append('circle')
      .attr('stroke', '#fff')
      .attr('r', 7)
      .attr('stroke-width', 8)
      .style('opacity', 0);

    setWidth(initialWidth);
  }

  function updateChart() {
    const svg = d3.select(`#chart-${id} svg g`),
      focus = svg.select('.focus circle'),
      chartContainer = svg.select('.chart-container'),
      sumstat = d3.group(data, (d) => d.group);

    svg.selectAll('.event-overlay').remove();

    if (hasLegend) {
      setLegend(
        Array.from(data).map((group, i) => {
          return {
            color: colors[i],
            group: group[0],
          };
        })
      );
    }

    const x = d3
      .scaleTime()
      .domain(
        d3.extent(data, function (d) {
          return d.key;
        })
      )
      .range([0, width]);
    svg
      .selectAll('.x-axis')
      .transition()
      .duration(1000)
      .call(
        d3
          .axisBottom(x)
          .tickFormat(xTicksFormatter ? xTicksFormatter : (d) => d)
      );

    svg
      .selectAll('.x-axis text')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .style('font-size', '12px');

    const y = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(data, function (d) {
          return +d.value;
        }),
      ])
      .range([height, 0]);

    svg
      .selectAll('.y-axis')
      .transition()
      .duration(1000)
      .call(d3.axisLeft(y).tickArguments([3, 's']));

    svg
      .selectAll('.y-axis path, .y-axis line, .x-axis path, .x-axis line')
      .remove();

    svg.selectAll('.tick text').attr('class', 'chart_tick_text');

    const color = d3
      .scaleOrdinal()
      .domain(['Positive', 'Negative'])
      .range(['#6FCF97', '#EB5757']);

    const line = chartContainer
      .selectAll('.line')
      .data(sumstat)
      .join('path')
      .attr('class', 'line')
      .attr('fill', 'none')
      .attr('stroke', (d) => color(d[0]))
      .attr('stroke-width', 1.5)
      .transition()
      .duration(1000)
      .attr('d', function (d) {
        return d3
          .line()
          .x(function (d) {
            return x(d.key);
          })
          .y(function (d) {
            return y(+d.value);
          })(d[1]);
      });

    function mouseover(e) {
      setTooltipVisible(true);
      focus.style('opacity', 1);
    }

    function mousemove(e) {
      let x0 = x.invert(d3.pointer(e)[0]),
        i = bisect(data, x0, 1),
        y0,
        selectedData,
        focusColor;
      if (
        i < data.length - 1 &&
        data[i].key.getTime() === data[i + 1].key.getTime()
      ) {
        y0 = y.invert(d3.pointer(e)[1]);
        selectedData =
          Math.abs(y0 - data[i].value) < Math.abs(y0 - data[i + 1].value)
            ? data[i]
            : data[i + 1];
        focusColor =
          Math.abs(y0 - data[i].value) < Math.abs(y0 - data[i + 1].value)
            ? colors[0]
            : colors[1];
      } else {
        selectedData = data[i];
        focusColor = colors[0];
      }

      const tooltipXposition = x(selectedData.key),
        tooltipYposition = y(selectedData.value);

      const options = { year: 'numeric', month: 'short' };
      setTooltipTitle(selectedData.key.toLocaleDateString('en-US', options));

      setTooltipValue(`${selectedData.group} - ${selectedData.value}`);
      focus
        .attr('cx', x(selectedData.key))
        .attr('cy', y(selectedData.value))
        .attr('fill', focusColor);
      setTooltipTop(tooltipYposition + margin.top + 'px');
      setTooltipLeft(tooltipXposition + margin.left + 'px');
    }

    function mouseleave(d) {
      if (
        d.toElement &&
        typeof d.toElement.className === 'string' &&
        d.toElement.className.includes('chart-tooltip')
      )
        return;
      setTooltipVisible(false);
      focus.style('opacity', 0);
    }

    svg
      .append('rect')
      .style('fill', 'none')
      .attr('class', 'event-overlay')
      .style('pointer-events', 'all')
      .attr('width', width + margin.right)
      .attr('height', height)
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseout', mouseleave);
  }

  useEffect(initChart, []);

  useEffect(updateChart, [data]);

  return (
    <div className="chart-wrapper">
      <div className="chart-container" id={`chart-${id}`}>
        <ChartTooltip
          top={-tooltipTop}
          left={tooltipLeft}
          title={tooltipTitle}
          visible={tooltipVisible}
          value={tooltipValue}
        />
      </div>
      <div className="download-btns">
        <DownloadAsExcelButton
          data={data}
          columns={columns}
          fileName={fileName}
        />
        <DownloadAsPNGButton
          chartId={`chart-${id}`}
          fileName="bar-chart.png"
          metadata={{
            title: title,
            filters: formatFilters(filters),
            downloadDate: new Date().toLocaleDateString(),
            websiteName: 'Mlondola AI for Tourism',
          }}
        />
      </div>
    </div>
  );
};

LineChart.propTypes = {};

LineChart.defaultProps = {};

export default LineChart;
